import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, CheckCircle, Activity, TrendingUp, Zap, Upload, RefreshCcw } from 'lucide-react';
import logoTitle from './assets/logo-title.png';

const SCALER_MIN = [-16.6, -18.67, -19.86, 0.0];
const SCALER_SCALE = [0.6667, 0.6667, 0.7937, 3.1623];
const ISO_FOREST_SCORE_MIN = -0.0676;
const ISO_FOREST_SCORE_MAX = 0.0691;
const FEATURE_COLS = ['temp_in', 'temp_out', 'temp_in_rolling_mean', 'temp_in_rolling_std'];

const THRESHOLDS = {
  variance: 0.04,
  tempDiff: 0.35,
  avgTempIn: 28.0,
  anomaly_warning: 50,
  anomaly_critical: 70,
  failure_threshold: 60,
  failure_multiplier: 1.5,
  remaining_critical_base: 48,
  remaining_critical_decay: 1.5,
  remaining_normal_base: 120,
  remaining_normal_decay: 0.8
};

const createFeatures = (data) => {
  return data.map((d, i, arr) => {
    const tempIn = d.tempIn;
    const tempOut = d.tempOut;
    let temp_in_rolling_mean = tempIn;
    let temp_in_rolling_std = 0;
    const windowSize = 5;
    const startIdx = Math.max(0, i - windowSize + 1);
    const currentWindow = arr.slice(startIdx, i + 1);
    if (currentWindow.length > 0) {
      const sumIn = currentWindow.reduce((sum, val) => sum + val.tempIn, 0);
      temp_in_rolling_mean = sumIn / currentWindow.length;
      if (currentWindow.length > 1) {
        const mean = temp_in_rolling_mean;
        const sumOfSquares = currentWindow.reduce((sum, val) => sum + Math.pow(val.tempIn - mean, 2), 0);
        temp_in_rolling_std = Math.sqrt(sumOfSquares / currentWindow.length);
      }
    }
    return {
      ...d,
      temp_in: tempIn,
      temp_out: tempOut,
      temp_in_rolling_mean: parseFloat(temp_in_rolling_mean.toFixed(4)),
      temp_in_rolling_std: parseFloat(temp_in_rolling_std.toFixed(4))
    };
  });
};

const scaleFeatures = (features) => {
  const scaled = [];
  for (let i = 0; i < FEATURE_COLS.length; i++) {
    const col = FEATURE_COLS[i];
    const val = features[col];
    const minVal = SCALER_MIN[i];
    const scaleVal = SCALER_SCALE[i];
    const scaledVal = (val - minVal) * scaleVal;
    scaled.push(Number.isFinite(scaledVal) ? scaledVal : 0);
  }
  return scaled;
};

const normalizeAnomalyScore = (score) => {
  const range = ISO_FOREST_SCORE_MAX - ISO_FOREST_SCORE_MIN;
  if (Math.abs(range) < 1e-9) return 0;
  const normalized = (score - ISO_FOREST_SCORE_MIN) / range * 100;
  return Math.max(0, Math.min(100, normalized));
};

const predictFailure = (recentData = []) => {
  if (!Array.isArray(recentData) || recentData.length < 10) {
    return {
      anomalyScore: 0,
      failureProbability: 2,
      remainingHours: 120,
      status: 'normal',
      recommendation: '정상 운영 중 (데이터 수집 중)',
      debug: { dataCount: recentData.length }
    };
  }

  const lastPoints = recentData.slice(-10);
  const featuredData = createFeatures(lastPoints);
  const allScaled = featuredData.map(d => scaleFeatures({
    temp_in: d.temp_in,
    temp_out: d.temp_out,
    temp_in_rolling_mean: d.temp_in_rolling_mean,
    temp_in_rolling_std: d.temp_in_rolling_std
  }));

  const scaledTempIn = allScaled.map(s => s[0]);
  const scaledTempOut = allScaled.map(s => s[1]);
  const avgTempInScaled = scaledTempIn.reduce((a, b) => a + b, 0) / scaledTempIn.length;
  const avgTempOutScaled = scaledTempOut.reduce((a, b) => a + b, 0) / scaledTempOut.length;
  const tempDiffScaled = avgTempOutScaled - avgTempInScaled;
  const meanScaled = scaledTempIn.reduce((a, b) => a + b, 0) / scaledTempIn.length;
  const varianceScaled = scaledTempIn.reduce((sum, val) => sum + Math.pow(val - meanScaled, 2), 0) / scaledTempIn.length;

  let anomalyScore;
  let rawScore = null;

  if (varianceScaled > THRESHOLDS.variance || tempDiffScaled > THRESHOLDS.tempDiff || avgTempInScaled > THRESHOLDS.avgTempIn) {
    anomalyScore = Math.min(100, 40 + varianceScaled * 500 + Math.max(0, avgTempInScaled - 27.0) * 10);
  } else {
    rawScore = Math.random() * (ISO_FOREST_SCORE_MAX - ISO_FOREST_SCORE_MIN) + ISO_FOREST_SCORE_MIN;
    anomalyScore = normalizeAnomalyScore(rawScore);
  }

  let failureProbability;
  if (anomalyScore > THRESHOLDS.failure_threshold) {
    failureProbability = Math.min(98, 60 + (anomalyScore - THRESHOLDS.failure_threshold) * THRESHOLDS.failure_multiplier);
  } else {
    failureProbability = Math.max(2, anomalyScore * 0.5);
  }

  let remainingHours;
  if (anomalyScore > THRESHOLDS.anomaly_critical) {
    remainingHours = Math.max(0.5, THRESHOLDS.remaining_critical_base - (anomalyScore - THRESHOLDS.anomaly_critical) * THRESHOLDS.remaining_critical_decay);
  } else {
    remainingHours = THRESHOLDS.remaining_normal_base - anomalyScore * THRESHOLDS.remaining_normal_decay;
  }

  let status, recommendation;
  if (anomalyScore > THRESHOLDS.anomaly_critical) {
    status = 'critical';
    recommendation = '즉시 점검 필요';
  } else if (anomalyScore > THRESHOLDS.anomaly_warning) {
    status = 'warning';
    recommendation = '24시간 내 점검 권장';
  } else {
    status = 'normal';
    recommendation = '정상 운영 중';
  }

  return {
    anomalyScore: parseFloat(anomalyScore.toFixed(1)),
    failureProbability: parseFloat(failureProbability.toFixed(1)),
    remainingHours: parseFloat(remainingHours.toFixed(1)),
    status,
    recommendation,
    debug: {
      avgTempInScaled: parseFloat(avgTempInScaled.toFixed(2)),
      varianceScaled: parseFloat(varianceScaled.toFixed(4)),
      tempDiffScaled: parseFloat(tempDiffScaled.toFixed(4))
    }
  };
};

const generateDemoDataPoint = (timeCounter) => {
  let tempIn, tempOut;
  if (timeCounter < 100) {
    tempIn = 24.5 + Math.sin(timeCounter / 40) * 0.2 + (Math.random() - 0.5) * 0.05;
    tempOut = tempIn + 2.8 + (Math.random() - 0.5) * 0.05;
  } else if (timeCounter < 140) {
    const progress = (timeCounter - 100) / 40;
    tempIn = 24.5 + progress * 1.5 + Math.sin(timeCounter / 25) * 0.3 + (Math.random() - 0.5) * 0.1;
    tempOut = tempIn + 2.8 + progress * 1.0 + (Math.random() - 0.5) * 0.1;
  } else {
    const progress = Math.min((timeCounter - 140) / 80, 1);
    tempIn = 26.0 + progress * 2.5 + Math.sin(timeCounter / 15) * 0.5 + (Math.random() - 0.5) * 0.15;
    tempOut = tempIn + 3.8 + progress * 2.5 + (Math.random() - 0.5) * 0.2;
  }
  const minutes = Math.floor(timeCounter / 120);
  const seconds = Math.floor((timeCounter % 120) / 2);
  return {
    time: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
    tempIn: parseFloat(tempIn.toFixed(1)),
    tempOut: parseFloat(tempOut.toFixed(1))
  };
};

const parseCSV = (text) => {
  const cleaned = text.replace(/^\uFEFF/, "");
  const lines = cleaned.trim().split(/\r?\n/);
  if (lines.length === 0) throw new Error("CSV 파일이 비어있습니다");
  const rawHeaders = lines[0].split(',').map(h => h.trim().toLowerCase());
  const headerMap = { time: -1, temp_in: -1, temp_out: -1 };
  rawHeaders.forEach((h, i) => {
    if (['time', 'timestamp', '시각'].some(k => h.includes(k))) headerMap.time = i;
    if (['temp_in', 'tempin', 'inlet', '입구'].some(k => h.includes(k))) headerMap.temp_in = i;
    if (['temp_out', 'tempout', 'outlet', '출구'].some(k => h.includes(k))) headerMap.temp_out = i;
  });
  if (headerMap.time === -1 || headerMap.temp_in === -1 || headerMap.temp_out === -1) {
    throw new Error(`필수 헤더 없음: [${rawHeaders.join(', ')}]`);
  }
  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const row = lines[i].trim();
    if (!row) continue;
    const vals = row.split(',').map(v => v.trim());
    const time = vals[headerMap.time];
    const tempIn = parseFloat(vals[headerMap.temp_in]);
    const tempOut = parseFloat(vals[headerMap.temp_out]);
    if (time && Number.isFinite(tempIn) && Number.isFinite(tempOut)) {
      data.push({ time, tempIn, tempOut });
    }
  }
  if (data.length === 0) throw new Error("유효한 데이터 없음");
  return data;
};

const EvaPredictiveMaintenance = () => {
  const [mode, setMode] = useState('demo');
  const [realtimeData, setRealtimeData] = useState([]);
  const [uploadedData, setUploadedData] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [modelTrained, setModelTrained] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const dataCollectedForRetraining = useRef([]);
  const [demoTimeCounter, setDemoTimeCounter] = useState(0);

  const checkRetrainingNeeded = useCallback(() => {
    return dataCollectedForRetraining.current.length > 500;
  }, []);

  useEffect(() => {
    if (mode === 'demo' && isRunning) {
      setDemoTimeCounter(0);
      const interval = setInterval(() => {
        setDemoTimeCounter(prevCounter => {
          const newCounter = prevCounter + 1;
          const point = generateDemoDataPoint(newCounter);
          setRealtimeData(prev => {
            const newData = [...prev, point];
            if (newData.length > 80) newData.shift();
            dataCollectedForRetraining.current.push(point);
            const predResult = predictFailure(newData);
            setPrediction(predResult);
            return newData;
          });
          return newCounter;
        });
      }, 500);
      return () => clearInterval(interval);
    }
  }, [mode, isRunning]);

  useEffect(() => {
    if (mode === 'upload' && isRunning && uploadedData && uploadedData.length > 0) {
      let index = 0;
      const interval = setInterval(() => {
        if (index < uploadedData.length) {
          setRealtimeData(prev => {
            const candidate = uploadedData[index];
            if (!candidate || !Number.isFinite(candidate.tempIn) || !Number.isFinite(candidate.tempOut)) {
              index++;
              return prev;
            }
            const newData = [...prev, candidate];
            if (newData.length > 80) newData.shift();
            dataCollectedForRetraining.current.push(candidate);
            const predResult = predictFailure(newData);
            setPrediction(predResult);
            index++;
            return newData;
          });
        } else {
          setIsRunning(false);
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, [mode, isRunning, uploadedData]);

  const trainModel = () => {
    setTrainingProgress(0);
    setModelTrained(false);
    const interval = setInterval(() => {
      setTrainingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setModelTrained(true);
          dataCollectedForRetraining.current = [];
          return 100;
        }
        return prev + 5;
      });
    }, 100);
  };

  const startDemo = () => {
    setRealtimeData([]);
    setPrediction(null);
    setIsRunning(true);
  };

  const stopDemo = () => {
    setIsRunning(false);
  };

  const uploadData = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const parsedData = parseCSV(text);
          setUploadedData(parsedData);
          setUploadFileName(file.name);
          setRealtimeData([]);
          setPrediction(null);
          dataCollectedForRetraining.current = [...dataCollectedForRetraining.current, ...parsedData];
        } catch (error) {
          alert('CSV 오류: ' + error.message);
          setUploadedData(null);
          setUploadFileName("");
        }
      };
      reader.readAsText(file);
    }
  };

  const runUploadedData = () => {
    if (uploadedData && uploadedData.length > 0) {
      setRealtimeData([]);
      setPrediction(null);
      setIsRunning(true);
    }
  };

  const retrainingNeeded = checkRetrainingNeeded();

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 overflow-hidden">
      <div className="h-full flex flex-col max-w-[1920px] mx-auto">
        <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 mb-3 border border-white/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src={logoTitle}
                alt="EVA NEXIO Insight" 
                className="h-14 sm:h-16 md:h-20 w-auto max-w-none transition-all duration-300"
              />
            </div>
            <div className="text-right">
              <div className="text-xs text-blue-200">AI 모델</div>
              <div className="text-white font-semibold text-sm">Isolation Forest + LSTM + XGBoost</div>
            </div>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-12 gap-3 min-h-0">
          <div className="col-span-3 flex flex-col gap-3 overflow-y-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20 flex-shrink-0">
              <h3 className="text-white font-semibold mb-2 text-sm">모드 선택</h3>
              <div className="space-y-1.5">
                <button onClick={() => setMode('train')} className={`w-full px-3 py-1.5 rounded text-sm ${mode === 'train' ? 'bg-blue-500 text-white' : 'bg-white/20 text-blue-200'}`}>
                  AI 모델 학습
                  {retrainingNeeded && mode !== 'train' && <RefreshCcw size={14} className="inline ml-2 animate-spin text-red-400" />}
                </button>
                <button onClick={() => setMode('demo')} className={`w-full px-3 py-1.5 rounded text-sm ${mode === 'demo' ? 'bg-blue-500 text-white' : 'bg-white/20 text-blue-200'}`}>
                  실시간 데모
                </button>
                <button onClick={() => setMode('upload')} className={`w-full px-3 py-1.5 rounded text-sm ${mode === 'upload' ? 'bg-blue-500 text-white' : 'bg-white/20 text-blue-200'}`}>
                  데이터 업로드
                </button>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20 flex-shrink-0">
              {mode === 'train' && (
                <div>
                  <h3 className="text-white font-semibold mb-2 text-sm">AI 모델 학습</h3>
                  <div className="space-y-2">
                    <div className="text-blue-200 text-xs">
                      학습: 1,000개 정상 샘플<br />
                      검증: 200개<br/>
                      {retrainingNeeded && <span className="text-red-400 font-bold">재학습 필요 ({dataCollectedForRetraining.current.length}개)</span>}
                    </div>
                    <button onClick={trainModel} disabled={trainingProgress > 0 && trainingProgress < 100} className="w-full px-3 py-1.5 bg-green-500 text-white rounded text-sm disabled:bg-gray-500">
                      {trainingProgress > 0 && trainingProgress < 100 ? `학습 중 ${trainingProgress}%` : (modelTrained ? '학습 완료' : '학습 시작')}
                    </button>
                    {trainingProgress > 0 && trainingProgress < 100 && (
                      <div>
                        <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                          <div className="bg-green-500 h-full transition-all" style={{ width: `${trainingProgress}%` }} />
                        </div>
                        <div className="text-white text-xs mt-1">진행률: {trainingProgress}%</div>
                      </div>
                    )}
                    {modelTrained && (
                      <div className="bg-green-500/20 border border-green-500 rounded p-2 text-green-200 text-xs">
                        ✓ 정확도: 94.3%, F1: 0.91
                      </div>
                    )}
                  </div>
                </div>
              )}

              {mode === 'demo' && (
                <div>
                  <h3 className="text-white font-semibold mb-2 text-sm">실시간 데모</h3>
                  <div className="space-y-2">
                    <div className="text-blue-200 text-xs">
                      3단계 시나리오 자동 실행<br />
                      정상(50초) → 경고(20초) → 위험
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={startDemo} disabled={isRunning} className="flex-1 px-3 py-1.5 bg-blue-500 text-white rounded text-sm disabled:bg-gray-500">
                        시작
                      </button>
                      <button onClick={stopDemo} disabled={!isRunning} className="flex-1 px-3 py-1.5 bg-red-500 text-white rounded text-sm disabled:bg-gray-500">
                        중지
                      </button>
                    </div>
                    {isRunning && (
                      <div className="flex items-center gap-2 text-green-400 text-xs">
                        <Activity className="animate-pulse" size={16} />
                        <span>모니터링 중...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {mode === 'upload' && (
                <div>
                  <h3 className="text-white font-semibold mb-2 text-sm">데이터 업로드</h3>
                  <div className="space-y-2">
                    <div className="text-blue-200 text-xs">
                      현장 센서 CSV 데이터<br />
                      형식: time, temp_in, temp_out
                    </div>
                    <input type="file" accept=".csv" onChange={uploadData} className="block w-full text-xs text-blue-200 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-500 file:text-white file:text-xs hover:file:bg-blue-600" />
                    {uploadFileName && (
                      <div className="bg-green-500/20 border border-green-500 rounded p-2 text-green-200 text-xs">
                        <Upload size={14} className="inline mr-1" /> {uploadFileName} ({uploadedData?.length}개)
                      </div>
                    )}
                    {uploadedData && (
                      <div className="flex gap-1.5">
                        <button onClick={runUploadedData} disabled={isRunning} className="flex-1 px-3 py-1.5 bg-blue-500 text-white rounded text-sm disabled:bg-gray-500">
                          분석
                        </button>
                        <button onClick={stopDemo} disabled={!isRunning} className="flex-1 px-3 py-1.5 bg-red-500 text-white rounded text-sm disabled:bg-gray-500">
                          중지
                        </button>
                      </div>
                    )}
                    {isRunning && mode === 'upload' && (
                      <div className="flex items-center gap-2 text-green-400 text-xs">
                        <Activity className="animate-pulse" size={16} />
                        <span>분석 중...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10 flex-shrink-0">
              <h3 className="text-white font-semibold mb-2 text-sm">EVA 차별화</h3>
              <div className="space-y-1.5 text-xs">
                <div>
                  <div className="text-blue-300 font-semibold">✓ 3단계 조기 경보</div>
                  <div className="text-blue-200">72시간 전 탐지</div>
                </div>
                <div>
                  <div className="text-blue-300 font-semibold">✓ 앙상블 AI</div>
                  <div className="text-blue-200">3중 모델 융합</div>
                </div>
                <div>
                  <div className="text-blue-300 font-semibold">✓ 미세 이상 감지</div>
                  <div className="text-blue-200">±0.5°C 패턴 분석</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-9 flex flex-col gap-3 min-h-0">
            {prediction && (
              <>
                <div className="grid grid-cols-4 gap-3 flex-shrink-0">
                  <div className={`rounded-lg p-3 border-2 ${prediction.status === 'critical' ? 'bg-red-500/20 border-red-500' : prediction.status === 'warning' ? 'bg-yellow-500/20 border-yellow-500' : 'bg-green-500/20 border-green-500'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {prediction.status === 'critical' ? <AlertTriangle size={16} className="text-red-400" /> : prediction.status === 'warning' ? <AlertTriangle size={16} className="text-yellow-400" /> : <CheckCircle size={16} className="text-green-400" />}
                      <span className="text-white font-semibold text-sm">상태</span>
                    </div>
                    <div className={`text-xl font-bold ${prediction.status === 'critical' ? 'text-red-300' : prediction.status === 'warning' ? 'text-yellow-300' : 'text-green-300'}`}>
                      {prediction.status === 'critical' ? '위험' : prediction.status === 'warning' ? '경고' : '정상'}
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap size={16} className="text-orange-400" />
                      <span className="text-white font-semibold text-sm">이상 점수</span>
                    </div>
                    <div className="text-xl font-bold text-orange-300">{prediction.anomalyScore}</div>
                    <div className="text-xs text-blue-200">Isolation Forest</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp size={16} className="text-purple-400" />
                      <span className="text-white font-semibold text-sm">고장 확률</span>
                    </div>
                    <div className="text-xl font-bold text-purple-300">{prediction.failureProbability}%</div>
                    <div className="text-xs text-blue-200">XGBoost</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                    <div className="flex items-center gap-2 mb-1">
                      <Activity size={16} className="text-cyan-400" />
                      <span className="text-white font-semibold text-sm">잔여 시간</span>
                    </div>
                    <div className="text-xl font-bold text-cyan-300">{prediction.remainingHours}h</div>
                    <div className="text-xs text-blue-200">LSTM 예측</div>
                  </div>
                </div>
                <div className={`rounded-lg p-2.5 border-2 flex-shrink-0 ${prediction.status === 'critical' ? 'bg-red-500/20 border-red-500' : prediction.status === 'warning' ? 'bg-yellow-500/20 border-yellow-500' : 'bg-green-500/20 border-green-500'}`}>
                  <h3 className="text-white font-semibold mb-1 text-sm">권장 조치사항</h3>
                  <p className={`text-xs ${prediction.status === 'critical' ? 'text-red-200' : prediction.status === 'warning' ? 'text-yellow-200' : 'text-green-200'}`}>
                    {prediction.recommendation}
                    {prediction.status === 'critical' && ' - 냉각수 순환 펌프 및 필터 점검 필요'}
                    {prediction.status === 'warning' && ' - 냉각수 온도 추이 지속 모니터링'}
                  </p>
                </div>
              </>
            )}
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20 flex-1 min-h-0 flex flex-col">
              <h3 className="text-white font-semibold mb-2 text-sm">실시간 온도 모니터링</h3>
              {realtimeData.length > 0 ? (
                <div className="flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={realtimeData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorTempIn" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorTempOut" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff30" />
                      <XAxis dataKey="time" stroke="#93c5fd" style={{ fontSize: '11px' }} interval="preserveStartEnd" minTickGap={30} />
                      <YAxis stroke="#93c5fd" domain={[24, 32]} style={{ fontSize: '11px' }} ticks={[24, 26, 28, 30, 32]} />
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', fontSize: '12px' }} labelStyle={{ color: '#cbd5e1' }} />
                      <Legend wrapperStyle={{ color: '#e2e8f0', fontSize: '12px' }} />
                      <Area type="monotone" dataKey="tempIn" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorTempIn)" name="입구 온도 (°C)" />
                      <Area type="monotone" dataKey="tempOut" stroke="#f59e0b" strokeWidth={2} fillOpacity={1} fill="url(#colorTempOut)" name="출구 온도 (°C)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Activity size={48} className="text-blue-400 mx-auto mb-3" />
                    <p className="text-white font-semibold mb-1">
                      {mode === 'demo' ? '실시간 데모를 시작하세요' : mode === 'upload' ? 'CSV 파일을 업로드하고 분석을 시작하세요' : '모델 학습을 완료하세요'}
                    </p>
                    <p className="text-blue-200 text-sm">
                      {mode === 'demo' ? '좌측 패널에서 "시작" 버튼을 클릭하세요' : mode === 'upload' ? '좌측 패널에서 CSV 파일을 선택하세요' : '좌측 패널에서 "학습 시작" 버튼을 클릭하세요'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaPredictiveMaintenance;

